import gql from "graphql-tag";
import {useMutation} from "@apollo/react-hooks";
import React, {useContext} from "react";
import {OrgDataContext} from "../../../context/orgData";
import {FlightNode} from "../../../__generated__/graphql/types";
import {Icon, message, Spin, TimePicker} from "antd";
import moment, {Moment} from "moment";
import {useEventTrigger} from "context/event-context";
import EditableText from "../../../components/editable-text";
import {exportTextInRef} from "../../util";

const MUTATION = gql`
    mutation EstDepartureHelper_UpdateFlight($payload: FlightNodeInput!, $orgID: ID!){
        setFlight(payload: $payload, orgID: $orgID) {
            _id
            estDepartureTime
        }
    }
`

type FlightRecord = Pick<FlightNode, '_id' | 'estDepartureTime' | 'state'>

interface EstDepartureHelperProps {
    record: FlightRecord
}

function useEstDeparture(record: FlightRecord){
    const [ mutate, { loading } ] = useMutation(MUTATION);
    const orgData = useContext(OrgDataContext);
    const trigger = useEventTrigger();
    const [ tempValue, setTempValue ] = React.useState<Moment | null>(null);

    function set(time: Moment){

        const payload = {
            _id: record._id,
            estDepartureTime: time.format('HH:mm')
        };

        setTempValue(time);

        mutate({
            variables: {
                payload: payload,
                orgID: orgData.getActiveOrgID()
            }
        })
            .then(({ data }) => {
                message.success("Est departure time updated");
                trigger("flight.estDepartureUpdated", data.setFlight);
                setTempValue(null);
            })
            .catch(err => {
                console.error("Failed to update est departure time", err);
                message.error("Failed to update est departuee time");
                setTempValue(null);
            });
    }

    let value = tempValue;
    if (!value){
        value = record.estDepartureTime ? moment.tz(record.estDepartureTime, 'HH:mm', 'America/Chicago') : null;
    }

    return {
        loading,
        value,
        set
    }
}

const EstDepartureHelper = React.forwardRef<any, EstDepartureHelperProps>(({ record }, ref) => {
    const { loading, set, value } = useEstDeparture(record);
    const escapePressed = React.useRef(false);
    const displayText = value ? `${value.format('HH:mm')} CST` : 'N/A';

    return (
        <Spin
            spinning={loading}
            indicator={<Icon type="loading" />}
            ref={exportTextInRef(displayText, ref)}
        >
            <EditableText
                disableEdit={record.state === 'DEPARTED' || record.state === 'LOCKED'}
                renderInput={(props) => (
                    <div
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                props.confirm(false);
                            }
                            if (e.key === 'Escape') {
                                escapePressed.current = true;
                                props.cancel();
                            }
                        }}
                    >
                        <TimePicker
                            value={props.value}
                            onChange={props.setInputValue}
                            onOpenChange={(open) => {
                                if (!open){
                                    setTimeout(() => {
                                        if (escapePressed.current) {
                                            escapePressed.current = false;
                                            return;
                                        }
                                        props.confirm(false);
                                    }, 100)
                                }
                            }}
                            open
                            format="HH:mm"
                            ref={props.ref}
                            disabled={loading}
                            focusOnOpen
                        />
                    </div>
                )}
                getValueFromChangeEvent={(timeValue) => timeValue}
                value={value}
                renderValue={() => displayText}
                onValueChange={(value) => set(value)}
                editTooltipMessage="ESC: Cancel | Enter/Click away: Confirm. Time is in CST."
            />
        </Spin>
    )
})

EstDepartureHelper.displayName = 'EstDepartureHelper'

export default EstDepartureHelper
